/**
 * Master Panel > Yardım Videoları (ekle / düzenle / sil).
 *
 * Tasarım ve akış MasterPlans.jsx ile aynı desende: liste + Dialog form +
 * AlertDialog silme onayı. Yeni bir tasarım sistemi getirilmez.
 *
 * Video kaynağı iki türlü:
 *   • YouTube → URL girilir, backend doğrular ve yalnızca video kimliğini saklar.
 *   • Sunucu  → dosya seçilir/çekilir, POST /api/media/help-video ile yüklenir,
 *               dönen media id kayda bağlanır.
 */
import { useEffect, useRef, useState } from "react";
import { api, notifyError } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { HelpVideosDialog } from "../../components/HelpVideosDialog";
import { toast } from "sonner";
import {
  Plus,
  Trash2,
  Loader2,
  Pencil,
  Youtube,
  Video,
  Upload,
  Eye,
} from "lucide-react";

const empty = {
  topic: "",
  description: "",
  source: "youtube",
  youtube_url: "",
  media_id: "",
  media_filename: "",
  is_active: true,
  sort_order: 0,
};

function fmtSize(n) {
  if (!n) return "";
  const mb = n / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(n / 1024)} KB`;
}

export default function MasterHelpVideos() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/master/help-videos");
      setList(data);
    } catch (e) {
      notifyError(e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const startCreate = () => {
    setForm({ ...empty, sort_order: list.length });
    setEditingId(null);
    setOpen(true);
  };

  const startEdit = (v) => {
    setForm({
      topic: v.topic || "",
      description: v.description || "",
      source: v.source || "youtube",
      // Ham URL saklanmıyor; düzenlemede kimlikten okunabilir bir URL üretilir.
      youtube_url: v.youtube_video_id
        ? `https://www.youtube.com/watch?v=${v.youtube_video_id}`
        : "",
      media_id: v.media_id || "",
      media_filename: v.media_filename || "",
      is_active: !!v.is_active,
      sort_order: v.sort_order ?? 0,
    });
    setEditingId(v.id);
    setOpen(true);
  };

  const pickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // aynı dosya tekrar seçilebilsin
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/media/help-video", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({
        ...f,
        source: "server",
        media_id: data.id,
        media_filename: data.filename || file.name,
      }));
      toast.success("Video yüklendi");
    } catch (err) {
      notifyError(err);
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.topic.trim()) {
      toast.error("Konu girin");
      return;
    }
    if (form.source === "youtube" && !form.youtube_url.trim()) {
      toast.error("YouTube video adresini girin");
      return;
    }
    if (form.source === "server" && !form.media_id) {
      toast.error("Önce video dosyası yükleyin");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        topic: form.topic.trim(),
        description: form.description.trim() || null,
        source: form.source,
        youtube_url: form.source === "youtube" ? form.youtube_url.trim() : null,
        media_id: form.source === "server" ? form.media_id : null,
        is_active: !!form.is_active,
        sort_order: Number(form.sort_order) || 0,
      };
      if (editingId) {
        await api.put(`/master/help-videos/${editingId}`, payload);
        toast.success("Video güncellendi");
      } else {
        await api.post("/master/help-videos", payload);
        toast.success("Video eklendi");
      }
      setOpen(false);
      await load();
    } catch (err) {
      notifyError(err);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/master/help-videos/${deleteId}`);
      toast.success("Video silindi");
      setDeleteId(null);
      await load();
    } catch (err) {
      notifyError(err);
    }
  };

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto space-y-6" data-testid="master-help-videos-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow text-amber-600">Destek</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-2">
            Yardım Videoları
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            Firma kullanıcıları bu videoları Pano ve Ayarlar sayfalarındaki
            "Bilgi ve Destek için" alanından görür. Pasif videolar onlara
            gösterilmez.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setPreviewOpen(true)}
            className="rounded-sm"
            data-testid="help-videos-preview-btn"
          >
            <Eye className="h-4 w-4 mr-2" /> Firma Görünümü
          </Button>
          <Button
            onClick={startCreate}
            className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
            data-testid="add-help-video-btn"
          >
            <Plus className="h-4 w-4 mr-2" /> Yeni Video
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16">
          <Loader2 className="h-6 w-6 animate-spin inline" />
        </div>
      ) : list.length === 0 ? (
        <Card className="p-12 text-center rounded-sm">
          <Video className="h-10 w-10 mx-auto text-muted-foreground" />
          <div className="font-display font-bold text-lg mt-3">Henüz video yok</div>
          <p className="text-sm text-muted-foreground mt-2">
            "Yeni Video" ile ilk yardım videosunu ekleyin.
          </p>
        </Card>
      ) : (
        <Card className="rounded-sm border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-foreground text-background">
                <tr>
                  <th className="text-right px-4 py-3 label-eyebrow !text-gray-300 w-16">Sıra</th>
                  <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Konu</th>
                  <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Kaynak</th>
                  <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Durum</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {list.map((v) => (
                  <tr key={v.id} data-testid={`help-video-row-${v.id}`}>
                    <td className="px-4 py-3 font-mono text-right text-muted-foreground">
                      {v.sort_order}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-semibold">{v.topic}</div>
                      {v.description && (
                        <div className="text-xs text-muted-foreground mt-0.5">{v.description}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {v.source === "youtube" ? (
                        <span className="inline-flex items-center gap-1.5">
                          <Youtube className="h-3.5 w-3.5 text-red-600" /> YouTube
                          <span className="font-mono text-muted-foreground">
                            {v.youtube_video_id}
                          </span>
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5">
                          <Video className="h-3.5 w-3.5 text-sky-600" /> Sunucu
                          <span className="text-muted-foreground">
                            {v.media_filename || "—"}
                            {v.media_size_bytes ? ` (${fmtSize(v.media_size_bytes)})` : ""}
                          </span>
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Badge
                        variant="outline"
                        className={`rounded-sm text-[10px] uppercase tracking-wider ${
                          v.is_active
                            ? "border-emerald-500/40 text-emerald-700"
                            : "border-slate-400/40 text-slate-500"
                        }`}
                      >
                        {v.is_active ? "Aktif" : "Pasif"}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-sm mr-1"
                        onClick={() => startEdit(v)}
                        data-testid={`edit-help-video-${v.id}`}
                        aria-label="Düzenle"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-sm border-destructive/40 text-destructive hover:bg-destructive/10"
                        onClick={() => setDeleteId(v.id)}
                        data-testid={`delete-help-video-${v.id}`}
                        aria-label="Sil"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Ekle / düzenle */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              {editingId ? "Video Düzenle" : "Yeni Yardım Videosu"}
            </DialogTitle>
            <DialogDescription>
              YouTube adresi verebilir veya sunucuya video dosyası yükleyebilirsiniz.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="help-video-form">
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Konu *</Label>
              <Input
                value={form.topic}
                onChange={(e) => setForm({ ...form, topic: e.target.value })}
                placeholder="Yeni emanet kaydı nasıl açılır?"
                className="rounded-sm"
                data-testid="help-video-topic"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Açıklama</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Kısa açıklama"
                className="rounded-sm"
                rows={2}
                data-testid="help-video-description"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="label-eyebrow">Video Kaynağı *</Label>
                <Select
                  value={form.source}
                  onValueChange={(v) => setForm({ ...form, source: v })}
                >
                  <SelectTrigger className="rounded-sm" data-testid="help-video-source">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="youtube">YouTube</SelectItem>
                    <SelectItem value="server">Sunucu (dosya yükle)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="label-eyebrow">Sıralama</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.sort_order}
                  onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                  className="rounded-sm font-mono"
                  data-testid="help-video-sort-order"
                />
                <p className="text-[10px] text-muted-foreground">Küçük sayı üstte görünür</p>
              </div>
            </div>

            {form.source === "youtube" ? (
              <div className="space-y-1.5">
                <Label className="label-eyebrow">YouTube Video Adresi *</Label>
                <Input
                  value={form.youtube_url}
                  onChange={(e) => setForm({ ...form, youtube_url: e.target.value })}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="rounded-sm font-mono"
                  data-testid="help-video-youtube-url"
                />
                <div className="border-l-2 border-sky-500 bg-sky-50 px-3 py-2 text-[11px] text-sky-900">
                  <b>Liste dışı (unlisted)</b> videolar sorunsuz izlenir.{" "}
                  <b>Özel (private)</b> videolar YouTube tarafından embed edilemez —
                  o videoları "Liste dışı" yapın.
                </div>
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label className="label-eyebrow">Video Dosyası *</Label>
                <input
                  ref={fileRef}
                  type="file"
                  // accept="video/*" + capture YOK: mobilde OS seçicisi
                  // "Video Çek / Galeri / Dosya" seçeneklerini birlikte sunar;
                  // masaüstünde normal dosya penceresi açılır. `capture`
                  // verilseydi mobilde yalnızca kamera açılırdı.
                  // İzin verilen gerçek türler backend'de doğrulanır.
                  accept="video/*"
                  onChange={pickFile}
                  className="hidden"
                  data-testid="help-video-file-input"
                />
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="rounded-sm"
                    data-testid="help-video-file-btn"
                  >
                    {uploading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4 mr-2" />
                    )}
                    {form.media_id ? "Dosyayı Değiştir" : "Dosya Seç / Video Çek"}
                  </Button>
                  {form.media_filename && (
                    <span className="text-xs text-muted-foreground truncate">
                      {form.media_filename}
                    </span>
                  )}
                </div>
                <p className="text-[10px] text-muted-foreground">
                  MP4 / WebM / MOV — en fazla 64 MB.
                </p>
              </div>
            )}

            <div className="flex items-center justify-between border-t border-border pt-3">
              <div>
                <Label className="label-eyebrow">Aktif</Label>
                <p className="text-[10px] text-muted-foreground">
                  Pasif videolar firma kullanıcılarına gösterilmez
                </p>
              </div>
              <Switch
                checked={!!form.is_active}
                onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                data-testid="help-video-is-active"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-sm">
                İptal
              </Button>
              <Button
                type="submit"
                disabled={saving || uploading}
                className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
                data-testid="help-video-submit"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editingId ? "Güncelle" : "Kaydet"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Firma görünümü önizlemesi — firma kullanıcısının gördüğü bileşen */}
      <HelpVideosDialog open={previewOpen} onOpenChange={setPreviewOpen} />

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display tracking-tight">
              Video silinsin mi?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Video listeden kaldırılır ve firma kullanıcılarına gösterilmez.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="rounded-sm bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-help-video"
            >
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
